import type {
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

const controlStyle = (error: boolean): CSSProperties => ({
  width: '100%',
  minWidth: 0,
  minHeight: 'var(--touch)',
  padding: '0 12px',
  borderRadius: 'var(--radius-sm)',
  border: `2px solid ${error ? 'var(--crave-red)' : 'var(--line)'}`,
  background: 'var(--paper)',
  color: 'var(--ink)',
});

interface Shell {
  label: ReactNode;
  hint?: string | undefined;
  error?: string | undefined;
  id: string;
  style?: CSSProperties | undefined;
  className?: string | undefined;
  children: ReactNode;
}

/**
 * Label, control and hint as three stacked rows. Inside a FormGrid the rows line up across columns
 * (CSS subgrid), so a long label or a hint in one column never pushes its neighbours out of line.
 */
function FieldShell({ label, hint, error, id, style, className, children }: Shell) {
  return (
    <label
      htmlFor={id}
      className={['crave-field', className].filter(Boolean).join(' ')}
      style={style}
    >
      <span className="crave-field-label">{label}</span>
      {children}
      <span
        id={`${id}-desc`}
        className="crave-field-hint"
        style={{ color: error ? 'var(--crave-red)' : undefined }}
      >
        {error ?? hint ?? ''}
      </span>
    </label>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Spans the whole FormGrid row. */
  wide?: boolean | undefined;
}

export function Field({ label, hint, error, id, style, className, wide, ...rest }: FieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      id={inputId}
      style={style}
      className={[className, wide ? 'crave-field-wide' : ''].filter(Boolean).join(' ')}
    >
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${inputId}-desc` : undefined}
        {...rest}
        style={controlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'children'
> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  wide?: boolean | undefined;
  /** [value, label] pairs. */
  options: readonly (readonly [string, string])[];
  /** Shown as the empty first option; omit to have no empty option. */
  placeholder?: string | undefined;
}

export function SelectField({
  label,
  hint,
  error,
  id,
  style,
  className,
  wide,
  options,
  placeholder,
  ...rest
}: SelectFieldProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      id={selectId}
      style={style}
      className={[className, wide ? 'crave-field-wide' : ''].filter(Boolean).join(' ')}
    >
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${selectId}-desc` : undefined}
        {...rest}
        style={{ ...controlStyle(Boolean(error)), height: 'var(--touch)', padding: '0 10px' }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  wide?: boolean | undefined;
}

export function TextAreaField({
  label,
  hint,
  error,
  id,
  style,
  className,
  wide = true,
  ...rest
}: TextAreaFieldProps) {
  const autoId = useId();
  const areaId = id ?? autoId;
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      id={areaId}
      style={style}
      className={[className, wide ? 'crave-field-wide' : ''].filter(Boolean).join(' ')}
    >
      <textarea
        id={areaId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${areaId}-desc` : undefined}
        rows={3}
        {...rest}
        style={{ ...controlStyle(Boolean(error)), padding: '10px 12px', resize: 'vertical' }}
      />
    </FieldShell>
  );
}

/**
 * Responsive columns of fields that stay aligned: every label, control and hint sits on the same
 * line as its neighbours. `min` is the narrowest a column may get before wrapping.
 */
export function FormGrid({
  children,
  min = 220,
  style,
  className,
}: {
  children: ReactNode;
  min?: number | undefined;
  style?: CSSProperties | undefined;
  className?: string | undefined;
}) {
  return (
    <div
      className={['crave-form-grid', className].filter(Boolean).join(' ')}
      style={{ '--form-col': `${min}px`, ...style } as CSSProperties}
    >
      {children}
    </div>
  );
}

/** A button (or buttons) placed in a FormGrid cell, lined up with the controls beside it. */
export function FormAction({
  children,
  wide,
}: {
  children: ReactNode;
  wide?: boolean | undefined;
}) {
  return (
    <div className={['crave-field', 'crave-form-action', wide ? 'crave-field-wide' : ''].join(' ')}>
      <span className="crave-field-label" aria-hidden="true" />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {children}
      </div>
      <span className="crave-field-hint" />
    </div>
  );
}
